<div class="outer">
  <h1>Admin Dashboard</h1>
  <p>Manage creator applications and review pending payouts.</p>

  <div *ngIf="isLoading" class="loading-container">
    <p>Loading admin data...</p>
    <!-- You could add a spinner component here -->
  </div>

  <div *ngIf="!isLoading && currentUser?.a4aAdmin">
    <!-- View Toggle Buttons -->
    <div class="view-toggle">
      <button
        (click)="view = 'applications'"
        [class.active]="view === 'applications'"
      >
        Creator Applications ({{ creatorApplications.length }})
      </button>
      <button (click)="view = 'payments'" [class.active]="view === 'payments'">
        Pending Payouts ({{ pendingPayments.length }})
      </button>
      <button
        (click)="view = 'subscriptions'"
        [class.active]="view === 'subscriptions'"
      >
        Subscription Options
      </button>
    </div>

    <!-- Creator Applications Section -->
    <div *ngIf="view === 'applications'" class="admin-section">
      <h2>Creator Applications</h2>
      <div
        *ngIf="creatorApplications.length > 0; else noApplications"
        class="table-container"
      >
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Social Links</th>
              <th class="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let app of creatorApplications">
              <td>{{ app.displayName }}</td>
              <td>{{ app.email }}</td>
              <td>
                <!-- Iterate over the socialLinks map -->
                <ng-container *ngFor="let link of app.socialLinks | keyvalue">
                  <a
                    *ngIf="link.value"
                    [href]="link.value"
                    target="_blank"
                    class="social-link-tag"
                  >
                    {{ link.key | titlecase }}
                  </a>
                </ng-container>
              </td>
              <td class="actions-column">
                <button
                  (click)="onApproveApplication(app)"
                  class="btn btn-approve"
                >
                  Approve
                </button>
                <button (click)="onDenyApplication(app)" class="btn btn-deny">
                  Deny
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <ng-template #noApplications>
        <p class="no-data-message">No pending creator applications.</p>
      </ng-template>
    </div>

    <!-- Pending Payouts Section -->
    <div *ngIf="view === 'payments'" class="admin-section">
      <h2>Pending Payouts</h2>
      <div
        *ngIf="pendingPayments.length > 0; else noPayments"
        class="table-container"
      >
        <table>
          <thead>
            <tr>
              <th>Creator</th>
              <th>Date Requested</th>
              <th class="text-right">Amount</th>
              <th class="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let payment of pendingPayments">
              <td>{{ payment.creatorName || payment.creatorId }}</td>
              <td>{{ payment.requestedDate.toDate() | date : "medium" }}</td>
              <td class="text-right">{{ payment.amount | currency }}</td>
              <td class="actions-column">
                <button
                  (click)="onApprovePayment(payment)"
                  class="btn btn-approve"
                >
                  Approve
                </button>
                <button (click)="onDenyPayment(payment)" class="btn btn-deny">
                  Deny
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <ng-template #noPayments>
        <p class="no-data-message">No pending payouts to review.</p>
      </ng-template>
    </div>

    <!-- Creator Subscription Options Section -->
    <div *ngIf="view === 'subscriptions'" class="admin-section">
      <h2>Creator Subscription Options</h2>
      <p>Manage the subscription tiers available for creators.</p>

      <div
        class="table-container"
        *ngIf="creatorOptions?.length > 0 || isAddingNewOption; else noOptions"
      >
        <table>
          <thead>
            <tr>
              <th>Tier Name</th>
              <th class="text-right">Pricing (Amount & Stripe ID)</th>
              <th>Description</th>
              <th>Features</th>
              <th class="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            <!-- ADD NEW OPTION FORM ROW -->
            <tr *ngIf="isAddingNewOption">
              <!-- This row is a complete form for adding a new tier -->
              <td>
                <input
                  type="text"
                  [(ngModel)]="newOptionData.name"
                  name="new-name"
                  required
                  class="form-control"
                />
              </td>
              <td class="text-right">
                <div class="price-grid">
                  <label>Monthly ($)</label>
                  <input
                    type="number"
                    [(ngModel)]="newOptionData.price.monthly"
                    name="new-price-monthly"
                    required
                    class="form-control"
                  />
                  <label>Monthly ID</label>
                  <input
                    type="text"
                    [(ngModel)]="newOptionData.priceIds.monthly"
                    name="new-priceId-monthly"
                    class="form-control"
                  />
                  <label>Yearly ($)</label>
                  <input
                    type="number"
                    [(ngModel)]="newOptionData.price.yearly"
                    name="new-price-yearly"
                    required
                    class="form-control"
                  />
                  <label>Yearly ID</label>
                  <input
                    type="text"
                    [(ngModel)]="newOptionData.priceIds.yearly"
                    name="new-priceId-yearly"
                    class="form-control"
                  />
                </div>
              </td>
              <td>
                <textarea
                  [(ngModel)]="newOptionData.description"
                  name="new-description"
                  class="form-control"
                ></textarea>
              </td>
              <td>
                <textarea
                  [ngModel]="newOptionData.features.join('\n')"
                  (ngModelChange)="newOptionData.features = $event.split('\n')"
                  name="new-features"
                  class="form-control"
                  rows="5"
                  placeholder="One feature per line"
                ></textarea>
              </td>
              <td class="actions-column">
                <button (click)="onSaveNewOption()" class="btn btn-approve">
                  Save
                </button>
                <button (click)="onCancel()" class="btn">Cancel</button>
              </td>
            </tr>

            <!-- DISPLAY/EDIT EXISTING OPTIONS -->
            <tr *ngFor="let option of creatorOptions; let i = index">
              <!-- Tier Name -->
              <td>
                <div
                  *ngIf="
                    editingCell?.optionId === option.id &&
                      editingCell?.field === 'name';
                    else displayName
                  "
                  class="edit-cell"
                >
                  <input
                    type="text"
                    [(ngModel)]="editedData"
                    class="form-control"
                  />
                </div>
                <ng-template #displayName>
                  <div
                    (click)="startEdit(option, 'name')"
                    class="editable-cell"
                  >
                    {{ option.name }}
                  </div>
                </ng-template>
              </td>

              <!-- Pricing -->
              <td class="text-right">
                <div
                  *ngIf="
                    editingCell?.optionId === option.id &&
                      editingCell?.field === 'price';
                    else displayPrice
                  "
                  class="edit-cell"
                >
                  <div class="price-grid">
                    <label>Monthly ($)</label>
                    <input
                      type="number"
                      [(ngModel)]="editedData.price.monthly"
                      name="price-monthly-{{ i }}"
                      required
                      class="form-control"
                    />
                    <label>Monthly ID</label>
                    <input
                      type="text"
                      [(ngModel)]="editedData.priceIds.monthly"
                      name="priceId-monthly-{{ i }}"
                      class="form-control"
                    />
                    <label>Yearly ($)</label>
                    <input
                      type="number"
                      [(ngModel)]="editedData.price.yearly"
                      name="price-yearly-{{ i }}"
                      required
                      class="form-control"
                    />
                    <label>Yearly ID</label>
                    <input
                      type="text"
                      [(ngModel)]="editedData.priceIds.yearly"
                      name="priceId-yearly-{{ i }}"
                      class="form-control"
                    />
                  </div>
                </div>
                <ng-template #displayPrice>
                  <div
                    (click)="startEdit(option, 'price')"
                    class="editable-cell"
                  >
                    <div class="price-grid-display">
                      <span
                        >Monthly: {{ option.price.monthly | currency }}</span
                      >
                      <small>{{ option.priceIds?.monthly }}</small>
                      <span>Yearly: {{ option.price.yearly | currency }}</span>
                      <small>{{ option.priceIds?.yearly }}</small>
                    </div>
                  </div>
                </ng-template>
              </td>

              <!-- Description -->
              <td>
                <div
                  *ngIf="
                    editingCell?.optionId === option.id &&
                      editingCell?.field === 'description';
                    else displayDescription
                  "
                  class="edit-cell"
                >
                  <textarea
                    [(ngModel)]="editedData"
                    class="form-control"
                    rows="4"
                  ></textarea>
                </div>
                <ng-template #displayDescription>
                  <div
                    (click)="startEdit(option, 'description')"
                    class="editable-cell description-cell"
                  >
                    {{ option.description || "Click to add description" }}
                  </div>
                </ng-template>
              </td>

              <!-- Features -->
              <td>
                <div
                  *ngIf="
                    editingCell?.optionId === option.id &&
                      editingCell?.field === 'features';
                    else displayFeatures
                  "
                  class="edit-cell"
                >
                  <textarea
                    [(ngModel)]="editedFeaturesAsString"
                    class="form-control"
                    rows="5"
                    placeholder="One feature per line"
                  ></textarea>
                </div>
                <ng-template #displayFeatures>
                  <div
                    (click)="startEdit(option, 'features')"
                    class="editable-cell"
                  >
                    <ul class="features-list">
                      <li *ngFor="let feature of option.features">
                        {{ feature }}
                      </li>
                      <li *ngIf="!option.features?.length" class="text-muted">
                        Click to add features
                      </li>
                    </ul>
                  </div>
                </ng-template>
              </td>

              <!-- Actions -->
              <td class="actions-column">
                <div
                  *ngIf="
                    editingCell?.optionId === option.id;
                    else displayActions
                  "
                >
                  <button (click)="onSave(option)" class="btn btn-approve">
                    Save
                  </button>
                  <button (click)="onCancel()" class="btn">Cancel</button>
                </div>
                <ng-template #displayActions>
                  <button
                    (click)="onDeleteOption(option)"
                    class="btn btn-deny"
                    [disabled]="isAddingNewOption || !!editingCell"
                  >
                    Delete
                  </button>
                </ng-template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="form-actions" style="margin-top: 1rem">
        <button
          type="button"
          (click)="onAddNewOption()"
          class="btn"
          [disabled]="!!editingCell || isAddingNewOption"
        >
          Add New Option
        </button>
      </div>

      <ng-template #noOptions>
        <p class="no-data-message">No subscription options found.</p>
        <button
          (click)="onAddNewOption()"
          class="btn"
          [disabled]="isAddingNewOption"
        >
          Add an Option
        </button>
      </ng-template>
    </div>
  </div>

  <div *ngIf="!isLoading && !currentUser?.a4aAdmin">
    <p class="no-data-message">You do not have permission to view this page.</p>
  </div>
</div>
